import {
	BadgeText,
	Box,
	CellulaFormSubmitData,
	DialogAlert,
	Divider,
	Flex,
	Form,
	InputTextArea,
	Label,
	Sheet,
	Text,
	TextLink,
} from '@cellula/react';
import { primitiveColorTokens, tokens } from '@cellula/react-theme-patient';
import { TopBar } from '@component/top_bar.component';
import { Title } from '@component/title.component';
import { withTranslation } from '@application/i18n/with_translation';
import { DataList } from '@component/data_list.component';
import { useResolve, useResolveController, withNewContainer } from '@visiba/instantiation';
import { ScreenConclusionController, SlotNotAvailableError } from './screen_conclusion.controller';
import { Async } from '@component/async.component';
import { ButtonArrange } from '@component/button_arrange.component';
import type { ClientPhysicalLocationApiModel, ClientResourceModel } from '@api_patient/generated/clientv2/models';
import { useEffect, useState } from 'react';
import { Payment } from '@feature/payment/payment.component';
import { BookingScreen, ViewBookingAnalyticActions, ViewBookingController } from '../view_booking.controller';
import { DateService } from '@application/time/date.service';
import { AvatarInfo } from '@component/avatar_info.component';
import { IframeSheet } from '@component/iframe_sheet.component';
import styled from 'styled-components';
import { FormCard } from '@feature/dynamic_form/form_card.component';
import { BookingBasketService, UnansweredFormsError } from '../booking_basket.service';
import { validateRequired } from '@application/validators';
import { FileUpload } from '@feature/file_manager/file_upload.component';
import { FileManagerServiceReadyState } from '@feature/file_manager/mod';
import { capitalizeFirstLetter } from '@module/capitalize_first_letter';
import { useAnalyticContext } from '@application/analytic/analytic_link/mod';

const StyledTermsOfServiceSection = styled.div`
	text-align: center;
	font: ${tokens.text.label.smallRegular};

	${tokens.breakpointQuery.small} {
		text-align: left;
	}
`;

const REASON_MAX_LENGTH = 4000;

export const ScreenConclusion = withNewContainer(function ScreenConclusion() {
	const controller = useResolveController(ScreenConclusionController);
	const bookingBasketService = useResolve(BookingBasketService);
	const viewBookingController = useResolveController(ViewBookingController);
	const [payment, setPayment] = useState<{ paymentBillId: number; paymentAmount: string; ticket: string } | null>(null);
	const [disableSubmit, setDisableSubmit] = useState<string | false>(false);
	const analytic = useAnalyticContext();

	const flowSettings = viewBookingController.flowSettings.value;
	if (flowSettings.flowState !== 'accessible') {
		return null;
	}

	function handleFileManagerStateChange(state: FileManagerServiceReadyState) {
		analytic.trackEvent('Changed "attachments"');
		setDisableSubmit(
			state === FileManagerServiceReadyState.NotReady
				? withTranslation('View.CreateNewMessage.ScreenConclusion.AttachedFilesNotReady')
				: false,
		);
	}

	async function handleSubmit(data: CellulaFormSubmitData) {
		const submitResult = await controller.submit({
			...data,
		});

		if (submitResult.err) {
			if (submitResult.err instanceof SlotNotAvailableError) {
				return false;
			}

			if (submitResult.err instanceof UnansweredFormsError) {
				return Err(withTranslation('View.Booking.ScreenConclusion.FormValidationError'));
			}

			return Err(withTranslation('View.Booking.ScreenConclusion.SubmitError'));
		}

		if (submitResult.ok.payment) {
			setPayment({
				paymentBillId: submitResult.ok.payment.billId,
				paymentAmount: submitResult.ok.payment.amount,
				ticket: submitResult.ok.ticket,
			});
		} else {
			viewBookingController.goToScreen(BookingScreen.Done, {
				ticket: submitResult.ok.ticket,
			});
		}

		return Ok();
	}

	function handleInvalidSubmit() {
		analytic.trackEvent(ViewBookingAnalyticActions.FormIsInvalid);
	}

	async function handlePaymentSuccess() {
		viewBookingController.goToScreen(BookingScreen.Done, {
			ticket: payment?.ticket,
		});

		return Ok();
	}

	function handlePaymentAbort() {
		setPayment(null);

		controller.abortPayment();
	}

	function handleMessageChange() {
		analytic.trackEvent('Changed "message"');
	}

	function handleSubmitClick(requirePayment: boolean) {
		return () => {
			if (requirePayment) {
				analytic.trackEvent('Clicked pay and book time slot');
			} else {
				analytic.trackEvent('Clicked book time slot');
			}
		};
	}

	const showReasonTextarea = !flowSettings.value.descriptionOfMatter;
	const showAttachFiles = flowSettings.value.attachFiles;

	return (
		<>
			<TopBar.Content goBack />

			<Flex grow={1} gap={tokens.spacing.x2}>
				<Async derive={controller.data}>
					{(data) => {
						return (
							<>
								<Flex grow={1} gap={tokens.spacing.x2}>
									<Title title={withTranslation('View.Booking.ScreenConclusion.Title')} titleSize='small' />

									<Form.Root options={{}} onSubmit={handleSubmit} onInvalid={handleInvalidSubmit}>
										<Flex gap={tokens.spacing.x3} grow={1}>
											{showReasonTextarea || showAttachFiles ? (
												<Flex gap={tokens.spacing.x2}>
													{showReasonTextarea ? (
														<InputTextArea
															name='reason'
															label={withTranslation('View.Booking.ScreenConclusion.Input.Label.Reason')}
															helperText={withTranslation('View.Booking.ScreenConclusion.Input.HelpText.Reason')}
															minHeight={120}
															maxHeight={400}
															autoHeight
															required
															characterCount={REASON_MAX_LENGTH}
															onBlur={handleMessageChange}
															options={{
																validate: {
																	required: (v) =>
																		validateRequired(v) ||
																		withTranslation('View.Booking.ScreenConclusion.ValidationError.Required.Reason'),
																},
																maxLength: {
																	value: REASON_MAX_LENGTH,
																	message: withTranslation('View.Booking.ScreenConclusion.ValidationError.MaxLength.Reason', {
																		maxLength: `${REASON_MAX_LENGTH}`,
																	}),
																},
															}}
														/>
													) : null}
													{showAttachFiles ? (
														<Flex gap={tokens.size.x1}>
															<fieldset style={{ display: 'contents' }}>
																<Label type='legend' optional>
																	{withTranslation('Form.Input.Label.AttachedFiles')}
																</Label>
																<FileUpload.Root
																	identityEndpointPath='client/v2/userfiles'
																	onStateChange={handleFileManagerStateChange}
																>
																	<FileUpload.List />
																	<FileUpload.DropZone>
																		<FileUpload.Button />
																	</FileUpload.DropZone>
																</FileUpload.Root>
															</fieldset>
														</Flex>
													) : null}
												</Flex>
											) : null}

											<TimeSlotInfo
												title={withTranslation('View.Booking.TimeSlotInfo.Title')}
												timeSlot={{
													price: data.timeSlot.Price?.Value ? data.timeSlot.Price?.Formatted : undefined,
													representativeNin: data.representativeNin ?? undefined,
													reasonForVisit: data.reasonForVisit ?? undefined,
													duration: data.timeSlot.Duration,
													physicalLocation: data.physicalLocation,
													resource: data.timeSlot.Resource,
													start: data.timeSlot.Start,
												}}
											/>

											{bookingBasketService.formManager.getForms().length > 0 ? (
												<Flex gap={tokens.spacing.x1}>
													<Text variant='labelSemiBold'>{withTranslation('Common.Label.AnsweredForms')}</Text>
													<Flex gap={tokens.spacing.x1}>
														{bookingBasketService.formManager.getForms().map((form) => (
															<FormCard key={form.formId} formWorker={form} analyticAction='' onCompleted={false} />
														))}
													</Flex>
												</Flex>
											) : null}

											<Flex grow='1' justify='flex-end' gap={tokens.spacing.x2}>
												<Form.Error />
												<StyledTermsOfServiceSection>
													{withTranslation('View.CreateNewMessage.ScreenConclusion.Terms.Part1')}{' '}
													<IframeSheet
														src={controller.termsOfServiceUrl.value ?? ''}
														trigger={
															<TextLink as='button' size='small'>
																{withTranslation('View.CreateNewMessage.ScreenConclusion.Terms.Link')}
															</TextLink>
														}
													/>{' '}
													{withTranslation('View.CreateNewMessage.ScreenConclusion.Terms.Part2')}
												</StyledTermsOfServiceSection>
												<ButtonArrange>
													<Form.SubmitButton
														disabled={!!disableSubmit}
														disabledText={disableSubmit || ''}
														onClick={handleSubmitClick(data.requirePayment)}
													>
														{data.requirePayment
															? withTranslation('View.Booking.ScreenConclusion.ButtonWithPayment')
															: withTranslation('View.Booking.ScreenConclusion.ButtonWithoutPayment')}
													</Form.SubmitButton>
												</ButtonArrange>
											</Flex>
										</Flex>
									</Form.Root>
								</Flex>
							</>
						);
					}}
				</Async>
			</Flex>
			{payment ? (
				<PaymentSheet
					billId={payment.paymentBillId}
					paymentAmount={payment.paymentAmount}
					onSuccess={handlePaymentSuccess}
					onAbort={handlePaymentAbort}
				/>
			) : null}
			{controller.showUnavailableTimeSlotDialog.value ? <TimeSlotNotAvailableDialog /> : null}
		</>
	);
});

function TimeSlotNotAvailableDialog() {
	const viewBookingController = useResolveController(ViewBookingController);
	const analyticCtx = useAnalyticContext();

	useEffect(
		function whenMount() {
			analyticCtx.trackEvent(ViewBookingAnalyticActions.OpenedTimeSlotNotAvailableDialog);
		},
		[analyticCtx],
	);

	function handleAbort() {
		analyticCtx.trackEvent(ViewBookingAnalyticActions.ClosedTimeSlotNotAvailableDialog);
		viewBookingController.goToScreen(BookingScreen.Location);
	}

	return (
		<DialogAlert
			isOpen
			header={withTranslation('View.Booking.BookingUnavailableDialog.Header')}
			description={withTranslation('View.Booking.BookingUnavailableDialog.Description')}
			abortText={withTranslation('View.Booking.BookingUnavailableDialog.Button')}
			onAbort={handleAbort}
		/>
	);
}

interface Props {
	billId: number;
	paymentAmount: string;
	onSuccess?: () => void;
	onAbort?: () => void;
}

function PaymentSheet({ paymentAmount, billId, onSuccess, onAbort }: Props): JSX.Element {
	return (
		<Sheet onClose={onAbort} defaultOpen variant='full-restricted'>
			<Payment billId={billId} paymentAmount={paymentAmount} onSuccess={onSuccess} onAbort={onAbort} />
		</Sheet>
	);
}

interface TimeSlotInfoProps {
	title?: string;
	timeSlot: {
		price?: string;
		representativeNin?: string;
		reasonForVisit?: string;
		duration: number;
		physicalLocation: ClientPhysicalLocationApiModel | null;
		resource: ClientResourceModel | null;
		start: string;
	};
}

export function TimeSlotInfo({ title, timeSlot }: TimeSlotInfoProps) {
	const dateService = useResolve(DateService);
	const startDate = dateService.newDate(timeSlot.start);
	const endDate = startDate.add(timeSlot.duration, 'minutes');

	return (
		<Box
			background={primitiveColorTokens.neutral[100]}
			border={`1px solid ${tokens.color.border.muted}`}
			borderRadius={tokens.borderRadius.small}
			padding={tokens.spacing.x2}
			gap={tokens.spacing.x2}
		>
			{title ? <Text variant='labelLargeSemiBold'>{title}</Text> : null}
			<DataList.Root>
				{timeSlot.price ? (
					<DataList.Item label={withTranslation('Common.Label.Cost')}>
						<BadgeText variant='info'>{timeSlot.price}</BadgeText>
					</DataList.Item>
				) : null}
				{timeSlot.representativeNin ? (
					<DataList.Item label={withTranslation('Common.Label.AgentFor')}>{timeSlot.representativeNin}</DataList.Item>
				) : null}
				{timeSlot.reasonForVisit ? (
					<DataList.Item label={withTranslation('Common.Label.Rfv')}>{timeSlot.reasonForVisit}</DataList.Item>
				) : null}
				{timeSlot.price || timeSlot.representativeNin || timeSlot.reasonForVisit ? <Divider offset={tokens.spacing.x1} /> : null}

				<DataList.Item label={withTranslation('View.Booking.TimeSlotInfo.Label.Date')} icon='calendar'>
					{capitalizeFirstLetter(startDate.format('dddd D MMMM YYYY'))}
				</DataList.Item>
				<DataList.Item label={withTranslation('View.Booking.TimeSlotInfo.Label.Time')} icon='clock'>
					{startDate.format('HH:mm')} - {endDate.format('HH:mm')} ({timeSlot.duration}{' '}
					{withTranslation('Common.Word.Minutes').toLocaleLowerCase()})
				</DataList.Item>
				{timeSlot.physicalLocation ? (
					<DataList.Item label={withTranslation('View.Booking.TimeSlotInfo.Label.Location')} icon='pinMark'>
						<div>
							{timeSlot.physicalLocation.Name}
							<br />
							{timeSlot.physicalLocation.Address}
							<br />
							{timeSlot.physicalLocation.PostalCode}
							<br />
							{timeSlot.physicalLocation.City}
						</div>
					</DataList.Item>
				) : (
					<DataList.Item label={withTranslation('View.Booking.TimeSlotInfo.Label.Type')} icon='video'>
						{withTranslation('View.Booking.TimeSlotInfo.Label.Type.Digital')}
					</DataList.Item>
				)}
				{timeSlot.resource ? (
					<>
						<Divider offset={tokens.spacing.x1} />

						<AvatarInfo
							name={timeSlot.resource.Name ?? undefined}
							title={timeSlot.resource.Title ?? undefined}
							avatarSrc={timeSlot.resource.ProfileImageUrl ?? undefined}
						/>
					</>
				) : null}
			</DataList.Root>
		</Box>
	);
}
